import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { sendPrint } from "@/lib/mobile";
import { saleReceipt, comandaBill, ticketSlips, testPage, orderSlip, partialReceipt } from "@/lib/receipts";

const SERVICES = ["000018f0-0000-1000-8000-00805f9b34fb", "e7810a71-73ae-499d-8c15-faa9aef0c3f2", "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  "0000ff00-0000-1000-8000-00805f9b34fb", "0000ffe0-0000-1000-8000-00805f9b34fb", "0000fee7-0000-1000-8000-00805f9b34fb", "0000ae30-0000-1000-8000-00805f9b34fb"];
const DEFAULTS = { enabled: false, mode: "browser", columns: 32, device_id: "", device_name: "" };
const key = (uid) => `m_bt_${uid}`;
export const RAWBT_STORE = "https://play.google.com/store/apps/details?id=ru.a402d.rawbtprinter";

export const btSupported = () => !!navigator.bluetooth;
export const rawbtSupported = () => /Android/i.test(navigator.userAgent);
export const btReady = (p) => !!p.enabled && (p.mode === "rawbt" ? rawbtSupported() : btSupported() && !!p.device_id);
export const getBt = (uid) => { try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(key(uid)) || "{}") }; } catch { return { ...DEFAULTS }; } };
export const saveBt = (uid, p) => localStorage.setItem(key(uid), JSON.stringify(p));
export const btError = (e) => ({
  NotFoundError: "Nenhuma impressora foi escolhida", NetworkError: "Não conectou. Ligue a impressora e deixe perto do celular",
  SecurityError: "O navegador bloqueou o Bluetooth", NotAllowedError: "Permissão de Bluetooth negada",
}[e?.name] || e?.message || "Falha na impressora Bluetooth");
export const printErr = (e) => (e?.response ? errMsg(e) : e?.message || "Falha ao imprimir");

let link = null;

async function connect(device) {
  const server = await device.gatt.connect();
  for (const s of await server.getPrimaryServices()) {
    for (const c of await s.getCharacteristics()) {
      if (c.properties.writeWithoutResponse || c.properties.write) { link = { device, char: c }; return link; }
    }
  }
  throw new Error("Essa impressora não aceita impressão por Bluetooth LE");
}

export async function btPermission() {
  try { return (await navigator.permissions.query({ name: "bluetooth" })).state; } catch { return "unknown"; }
}
export async function btAvailable() {
  try { return navigator.bluetooth.getAvailability ? await navigator.bluetooth.getAvailability() : true; } catch { return true; }
}
// Só as impressoras que este site já recebeu permissão para usar (o navegador não vê a lista de pareados do Android).
export async function allowedPrinters() {
  try { return navigator.bluetooth.getDevices ? await navigator.bluetooth.getDevices() : []; } catch { return []; }
}

export async function choosePrinter(device) {
  await connect(device);
  return { device_id: device.id, device_name: device.name || "Impressora Bluetooth" };
}

export async function pairPrinter() {
  return choosePrinter(await navigator.bluetooth.requestDevice({ acceptAllDevices: true, optionalServices: SERVICES }));
}

async function ensure(p) {
  if (link?.device.id === p.device_id) return link.device.gatt.connected ? link : connect(link.device);
  const known = navigator.bluetooth.getDevices ? await navigator.bluetooth.getDevices() : [];
  const d = known.find((x) => x.id === p.device_id);
  if (d) return connect(d);
  throw Object.assign(new Error("Conecte a impressora Bluetooth"), { code: "pair" });
}

const toBytes = (out) => Uint8Array.from(out.filter((x) => typeof x === "string").join(""), (ch) => ch.charCodeAt(0) & 0xff);

async function write(char, bytes) {
  for (let i = 0; i < bytes.length; i += 100) {
    const part = bytes.slice(i, i + 100);
    if (char.properties.writeWithoutResponse) await char.writeValueWithoutResponse(part); else await char.writeValue(part);
    await new Promise((r) => setTimeout(r, 25));
  }
}

function rawbtSend(bytes) {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 4096) bin += String.fromCharCode(...bytes.subarray(i, i + 4096));
  window.location.href = `intent:base64,${btoa(bin)}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
}

const prFor = (p) => ({ name: p.mode === "rawbt" ? "RawBT" : p.device_name, columns: Number(p.columns) || 32, logo: false, cut: true });
const btPrint = async (p, build) => {
  const bytes = toBytes(build(prFor(p)));
  if (p.mode === "rawbt") return rawbtSend(bytes);
  const { char } = await ensure(p);
  return write(char, bytes);
};
export const btTest = (p, store) => btPrint(p, (pr) => testPage(pr, store));

const BUILD = {
  sale: (pl, t) => (pr) => saleReceipt(pl.sale, t, pr),
  bill: (pl, t) => (pr) => comandaBill(pl.comanda, t, pr),
  partial: (pl, t) => (pr) => partialReceipt(pl.partial, t, pr),
  ticket: (pl, t) => (pr) => ticketSlips(pl.sale, t, pr),
  order: (pl, t) => (pr) => orderSlip(pl.order, "PEDIDO", pl.order.bluetooth || pl.order.items, t.nome_fantasia, pr),
};

async function reconnectAndPrint(uid, p, build) {
  try {
    const np = { ...p, ...(await pairPrinter()) };
    saveBt(uid, np);
    await btPrint(np, build);
    toast.success("Impresso na impressora Bluetooth");
  } catch (e) { toast.error(btError(e)); }
}

// Conta, comprovantes e tickets vão para a impressora Bluetooth quando ligada; o resto vai para a fila do caixa.
export async function printDoc(session, kind, title, payload, { quiet = false } = {}) {
  const uid = session.user.id;
  const p = getBt(uid);
  if (!(btReady(p) && BUILD[kind])) {
    if (kind === "ticket" || kind === "order") return "none";
    await sendPrint(kind, title, payload);
    if (!quiet) toast.success("Enviado para a impressora do caixa");
    return "queue";
  }
  const build = BUILD[kind](payload, session.tenant);
  try {
    await btPrint(p, build);
    if (!quiet) toast.success(p.mode === "rawbt" ? "Enviado para o RawBT" : "Impresso na impressora Bluetooth");
    return "bt";
  } catch (e) {
    if (e.code !== "pair") throw new Error(btError(e));
    toast("Toque em Conectar para imprimir na impressora Bluetooth", { duration: 20000, action: { label: "Conectar", onClick: () => reconnectAndPrint(uid, p, build) } });
    return "pending";
  }
}
